/**
 * Proper easing functions for Framer Motion animations
 * These replace string values like "easeIn", "easeOut", "easeInOut" with proper cubic-bezier arrays
 */

// Standard easing curves as cubic-bezier arrays [x1, y1, x2, y2]
export const EASING = {
  // Standard ease functions
  easeIn: [0.4, 0, 1, 1] as const,
  easeOut: [0, 0, 0.2, 1] as const,
  easeInOut: [0.4, 0, 0.2, 1] as const,

  // Additional common easing curves
  ease: [0.25, 0.1, 0.25, 1] as const,
  linear: [0, 0, 1, 1] as const,

  // Smooth animations
  smooth: [0.4, 0, 0.2, 1] as const,

  // Bounce and spring-like effects
  bounceOut: [0.175, 0.885, 0.32, 1.275] as const,
  backOut: [0.175, 0.885, 0.32, 1.275] as const,

  // Custom curves for specific animations
  slideIn: [0.25, 0.46, 0.45, 0.94] as const,
  slideOut: [0.55, 0.085, 0.68, 0.53] as const,
} as const;

// Type for easing values
export type EasingValue = (typeof EASING)[keyof typeof EASING];

/**
 * Helper function to get easing value by name
 * @param name - The easing name
 * @returns The cubic-bezier array for the easing
 */
export function getEasing(name: keyof typeof EASING): EasingValue {
  return EASING[name];
}

/**
 * Common transition presets with proper easing
 */
export const TRANSITIONS = {
  // Quick animations
  quick: {
    duration: 0.2,
    ease: EASING.easeOut,
  },

  // Standard animations
  default: {
    duration: 0.3,
    ease: EASING.easeInOut,
  },

  // Smooth animations
  smooth: {
    duration: 0.4,
    ease: EASING.smooth,
  },

  // Slow animations
  slow: {
    duration: 0.5,
    ease: EASING.easeInOut,
  },

  // Fade animations
  fade: {
    duration: 0.3,
    ease: EASING.easeOut,
  },

  // Scale animations
  scale: {
    duration: 0.2,
    ease: EASING.easeOut,
  },

  // Slide animations
  slide: {
    duration: 0.4,
    ease: EASING.slideIn,
  },
} as const;
